// The chat element (impire-hq 02-DESIGN/14-solutions/03-the-console.md § the
// elements, *chat*, § the words; 02-DESIGN/19-chat/01-the-chat-solution.md
// § the Chat panel, § an escalated turn; hq decisions 0079 and 0083): the
// inference transcript on a streaming verb, drawn by the console like every
// element. The person's threads from the solution's threads verb, one thread
// with its turns from the thread verb — the same thread on any browser, the
// console keeping none of it but the person's last model — a model's turn
// rendered as markdown without raw HTML, script or image; the model picker
// from the models verb; the composer; the reply growing as the frames arrive,
// a quiet line while the model thinks, *Stop* while it writes; how many older
// turns the assistant left out; a refusal in the console's words where the
// reply would be, and a `guard-escalated` as the Approvals card in the
// thread, whose *Approve and send* sends the same transcript again. Every
// call is the person's own, on their connection, addressed to the role's
// workload.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Button, Confirm, Dot, Notice, Select } from "../../../ui/components.tsx";
import { serviceOf, type Element } from "../../../wire/solutions.ts";
import { Escalated } from "../../approvals/Escalated.tsx";
import { useSurface } from "../../surface.tsx";
import type { Ctx } from "../Elements.tsx";
import { codeOf, Relay } from "./relay.ts";
import {
  apply as applyReply,
  ask,
  CODE_CANCELLED,
  drain,
  empty as emptyReply,
  noteOf,
  words,
  type Note,
  type Reply,
  type Sending,
} from "./reply.ts";
import { forgetThreads, loadPrefs, savePrefs, scopeOf } from "./storage.ts";
import {
  sendBody,
  written,
  type Model,
  type SendBody,
  type Summary,
  type Thread,
  type Turn,
} from "./wire.ts";
import "./chat.css";

/** Streaming is the turn in flight: on which thread — empty until the first frame names a new one — the person's text, the reply so far, and how to stop it. */
interface Streaming {
  key: string;
  /** The person's text; empty for a resend, which appends no turn. */
  text: string;
  reply: Reply;
  abort: AbortController;
}

/** relative says how long ago, in a word or two. */
export function relative(at: number, now = Date.now()): string {
  if (!Number.isFinite(at)) {
    return "";
  }
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 60) {
    return "now";
  }
  const m = Math.round(s / 60);
  if (m < 60) {
    return `${m}m`;
  }
  const h = Math.round(m / 60);
  if (h < 24) {
    return `${h}h`;
  }
  const d = Math.round(h / 24);
  return d < 30 ? `${d}d` : new Date(at).toLocaleDateString();
}

/** ChatEl is the element: it resolves the role and its six verbs, then draws the chat. */
export function ChatEl({ ctx, e }: { ctx: Ctx; e: Element }) {
  const s = useSurface();
  const id = e.role !== undefined ? ctx.ids[e.role] : undefined;
  const relay = useMemo(() => {
    const { verb, cancel, models, threads, thread, clear } = e;
    if (
      id === undefined ||
      verb === undefined ||
      cancel === undefined ||
      models === undefined ||
      threads === undefined ||
      thread === undefined ||
      clear === undefined
    ) {
      return undefined;
    }
    return new Relay(s.bus, serviceOf(ctx.m, e.role ?? "", id), id, {
      send: verb,
      cancel,
      models,
      threads,
      thread,
      clear,
    });
  }, [s.bus, ctx.m, e, id]);
  if (relay === undefined) {
    return <Notice>This chat names no workload that runs here.</Notice>;
  }
  return (
    <Chat key={`${s.tenant}/${ctx.m.name}/${id ?? ""}`} relay={relay} scope={scopeOf(s.tenant, ctx.m.name)} />
  );
}

/** local is the thread with a turn's written turns applied from what streamed: what is shown where the thread cannot be read again. */
function local(t: Thread, text: string, reply: Reply, instance: string): Thread {
  const at = new Date().toISOString();
  let n = t.turns.length === 0 ? 0 : (t.turns[t.turns.length - 1]?.n ?? 0);
  const turns = [...t.turns];
  if (text !== "" && !written(t, text)) {
    turns.push({ n: ++n, role: "user", text, at });
  }
  const refused = reply.error !== undefined && reply.error.code !== CODE_CANCELLED;
  if (!(refused && reply.text === "")) {
    const a: Turn = { n: n + 1, role: "assistant", text: reply.text, at, model: instance };
    if (reply.outcome !== undefined) {
      a.usage = reply.outcome.usage;
    }
    if (reply.error?.code === CODE_CANCELLED) {
      a.cancelled = true;
    }
    if (reply.lost === true) {
      a.lost = true;
    }
    turns.push(a);
  }
  return { ...t, turns, updated: at };
}

function Chat({ relay, scope }: { relay: Relay; scope: string }) {
  const s = useSurface();
  const store = window.localStorage;
  const [threads, setThreads] = useState<Summary[] | undefined>(undefined);
  const [listError, setListError] = useState<string | undefined>(undefined);
  const [activeId, setActiveId] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState<Thread | undefined>(undefined);
  const [openError, setOpenError] = useState<string | undefined>(undefined);
  const [models, setModels] = useState<Model[] | undefined>(undefined);
  const [modelsError, setModelsError] = useState<string | undefined>(undefined);
  const [prefModel, setPrefModel] = useState<string | undefined>(() => loadPrefs(store, scope).model);
  const [pick, setPick] = useState<string | undefined>(undefined);
  const [streaming, setStreaming] = useState<Streaming | undefined>(undefined);
  const [notes, setNotes] = useState<Readonly<Record<string, Note>>>({});
  const [connected, setConnected] = useState(s.bus.current().kind === "connected");

  // Threads an earlier console kept in this browser are forgotten: the solution keeps them now.
  useEffect(() => {
    forgetThreads(store, scope);
  }, [store, scope]);

  useEffect(
    () =>
      s.bus.onChange((st) => {
        setConnected(st.kind === "connected");
      }),
    [s.bus],
  );

  const activeRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    activeRef.current = activeId;
  }, [activeId]);

  const fail = (err: unknown) => {
    const c = codeOf(err);
    return words(c.code, c.message);
  };

  const readModels = useCallback(() => {
    relay.models().then(
      (ms) => {
        setModels(ms);
        setModelsError(undefined);
      },
      (err: unknown) => {
        const c = codeOf(err);
        setModelsError(words(c.code, c.message));
        setModels([]);
      },
    );
  }, [relay]);

  const readList = useCallback(
    () =>
      relay.threads().then(
        (ts) => {
          setThreads(ts);
          setListError(undefined);
          return ts;
        },
        (err: unknown) => {
          const c = codeOf(err);
          setListError(words(c.code, c.message));
          return undefined;
        },
      ),
    [relay],
  );

  // The thread read last wins: a slower answer for a thread left behind is dropped.
  const reading = useRef(0);
  const select = useCallback(
    (id: string | undefined) => {
      const seq = ++reading.current;
      setActiveId(id);
      activeRef.current = id;
      setPick(undefined);
      setOpen(undefined);
      setOpenError(undefined);
      if (id === undefined) {
        return;
      }
      relay.thread(id).then(
        (t) => {
          if (reading.current === seq) {
            setOpen(t);
          }
        },
        (err: unknown) => {
          if (reading.current === seq) {
            const c = codeOf(err);
            setOpenError(words(c.code, c.message));
          }
        },
      );
    },
    [relay],
  );

  // On open: the models, the threads, the newest thread opened. On a reconnect, the models and the list again.
  const started = useRef(false);
  useEffect(() => {
    if (!connected) {
      return;
    }
    readModels();
    void readList().then((ts) => {
      if (!started.current) {
        started.current = true;
        if (activeRef.current === undefined && ts !== undefined && ts.length > 0) {
          select(ts[0]?.id);
        }
      }
    });
  }, [connected, readModels, readList, select]);

  // A reply in flight is stopped when the panel is left.
  const streamingRef = useRef<Streaming | undefined>(undefined);
  useEffect(() => {
    streamingRef.current = streaming;
  }, [streaming]);
  useEffect(
    () => () => {
      streamingRef.current?.abort.abort();
    },
    [],
  );

  const viewKey = activeId ?? "";
  const shown = open !== undefined && open.id === activeId ? open : undefined;
  const defaultModel = useMemo(() => {
    if (models === undefined || models.length === 0) {
      return undefined;
    }
    const pref = models.find((m) => m.workload === prefModel);
    return (pref ?? models.find((m) => m.default) ?? models[0])?.workload;
  }, [models, prefModel]);
  const modelOf = (id: string | undefined) => models?.find((m) => m.workload === id);
  const modelName = (id: string | undefined) => {
    const m = modelOf(id);
    return m === undefined ? undefined : m.display_name || m.name;
  };
  const chosenModel = modelOf(pick)?.workload ?? modelOf(shown?.model)?.workload ?? defaultModel;

  const setNote = (key: string, n: Note | undefined) => {
    setNotes((ns) => {
      const next = Object.fromEntries(Object.entries(ns).filter(([k]) => k !== key));
      if (n !== undefined) {
        next[key] = n;
      }
      return next;
    });
  };

  const chooseModel = (workload: string) => {
    setPrefModel(workload);
    setPick(workload);
    savePrefs(store, scope, { model: workload });
  };

  /**
   * run sends one turn — the person's text on the open thread, or on a new one
   * — or the same transcript again, and folds the reply in as it streams;
   * once it ends, the thread and the list are read again, so what the thread
   * shows is what the assistant wrote.
   */
  const queued = useRef<SendBody[]>([]);
  const run = (body: SendBody) => {
    if (streamingRef.current !== undefined || !connected) {
      return;
    }
    const before = new Set((threads ?? []).map((t) => t.id));
    const abort = new AbortController();
    const st: Streaming = { key: body.thread, text: body.text, reply: emptyReply, abort };
    streamingRef.current = st;
    setNote(body.thread, undefined);
    setStreaming(st);
    void (async () => {
      let reply = emptyReply;
      for await (const ev of relay.send(body, abort.signal)) {
        reply = applyReply(reply, ev);
        if (ev.kind === "thread" && ev.thread !== "" && body.thread === "") {
          // A new thread: the first frame named it; it is the open one and listed.
          if (activeRef.current === undefined) {
            setActiveId(ev.thread);
            activeRef.current = ev.thread;
          }
          void readList();
        }
        const r = reply;
        setStreaming((cur) => (cur?.abort === abort ? { ...cur, key: r.thread || cur.key, reply: r } : cur));
      }
      let id = reply.thread || body.thread;
      if (id === "") {
        // Refused before the first frame on a new thread: whether the assistant wrote one is read from the list.
        const ts = await readList();
        id = ts?.find((t) => !before.has(t.id))?.id ?? "";
      } else {
        void readList();
      }
      let t: Thread | undefined;
      if (id !== "") {
        t = await relay.thread(id).catch(() => undefined);
        if (t === undefined && open?.id === id) {
          t = local(open, body.text, reply, body.instance);
        }
      }
      const held = t !== undefined ? written(t, body.text) : reply.thread !== "";
      setNote(id, noteOf(reply, body, id, held));
      if (
        id !== "" &&
        (activeRef.current === id || (body.thread === "" && activeRef.current === undefined))
      ) {
        setActiveId(id);
        activeRef.current = id;
        if (t !== undefined) {
          setOpen(t);
        }
      }
      streamingRef.current = undefined;
      setStreaming(undefined);
      // A resend an approval granted while this reply streamed runs now, for its own thread.
      const d = drain(queued.current);
      queued.current = d.queued;
      if (d.now !== undefined) {
        runRef.current(d.now);
      }
    })();
  };
  const runRef = useRef(run);
  useEffect(() => {
    runRef.current = run;
  });

  /** resend sends an approval's retry: at once, or once the reply in flight ends — never dropped. */
  const resend = (body: SendBody): Sending => {
    const p = ask(streamingRef.current !== undefined, body, queued.current);
    queued.current = p.queued;
    if (p.now !== undefined) {
      run(p.now);
      return "sent";
    }
    return "queued";
  };

  const send = (text: string) => {
    if (chosenModel === undefined) {
      return;
    }
    run(sendBody(activeId ?? "", text, chosenModel));
  };

  const stop = () => {
    if (streaming === undefined) {
      return;
    }
    const request = streaming.reply.request;
    streaming.abort.abort();
    if (request !== "") {
      relay.cancel(request).catch(() => undefined);
    }
  };

  const threadOptions = [
    { value: "", label: "New thread" },
    ...(threads ?? []).map((c) => ({ value: c.id, label: c.title || "Untitled" })),
  ];
  const modelOptions =
    models === undefined
      ? [{ value: "", label: "Reading the models…" }]
      : models.length === 0
        ? [{ value: "", label: "No model to talk to" }]
        : models.map((m) => ({
            value: m.workload,
            label: `${m.display_name || m.name}${m.default ? " · default" : ""}`,
          }));
  const here = streaming !== undefined && streaming.key === viewKey ? streaming : undefined;
  const note = here === undefined ? notes[viewKey] : undefined;
  const retry = note?.retry;
  return (
    <div className="chat">
      <ThreadList
        threads={threads}
        error={listError}
        activeId={activeId}
        onNew={() => {
          select(undefined);
        }}
        onSelect={select}
        onRename={(id, title) => {
          relay.rename(id, title).then(
            () => {
              setThreads((ts) => ts?.map((c) => (c.id === id ? { ...c, title } : c)));
              setOpen((o) => (o?.id === id ? { ...o, title } : o));
            },
            (err: unknown) => {
              setListError(fail(err));
            },
          );
        }}
        onDelete={(id) => {
          relay.remove(id).then(
            () => {
              setThreads((ts) => ts?.filter((c) => c.id !== id));
              setNote(id, undefined);
              if (activeRef.current === id) {
                select(undefined);
              }
            },
            (err: unknown) => {
              setListError(fail(err));
            },
          );
        }}
        onClear={() => {
          relay.clear().then(
            () => {
              setThreads([]);
              setNotes({});
              select(undefined);
            },
            (err: unknown) => {
              setListError(fail(err));
            },
          );
        }}
      />
      <div className="chat-main">
        <div className="chat-threads-narrow">
          <Select
            value={activeId ?? ""}
            onChange={(v) => {
              select(v === "" ? undefined : v);
            }}
            options={threadOptions}
          />
        </div>
        <div className="chat-bar">
          <Select
            value={chosenModel ?? ""}
            onChange={chooseModel}
            options={modelOptions}
            disabled={models === undefined || models.length === 0 || streaming !== undefined}
          />
          <span className="grow" />
          {modelsError !== undefined && (
            <Button kind="quiet" size="sm" onClick={readModels}>
              {modelsError} · retry
            </Button>
          )}
          <span className="row small muted">
            <Dot tone={connected ? "live" : "bad"} />
            {connected ? "connected" : "reconnecting…"}
          </span>
        </div>
        <ThreadView
          opened={activeId !== undefined}
          turns={shown?.turns ?? []}
          reading={
            activeId !== undefined && shown === undefined && openError === undefined && here === undefined
          }
          openError={openError}
          streaming={here}
          note={note}
          model={modelName(chosenModel)}
          modelName={modelName}
          card={
            note?.escalation !== undefined ? (
              <Escalated
                key={note.escalation}
                id={note.escalation}
                resend={retry !== undefined ? () => resend(retry) : undefined}
              />
            ) : undefined
          }
        />
        <Composer
          disabled={!connected || chosenModel === undefined}
          streaming={streaming !== undefined}
          onSend={send}
          onStop={stop}
        />
      </div>
    </div>
  );
}

/** Rendered is a model's turn as markdown: the console's renderer admits no raw HTML, no script and no image; a link opens outside the console. */
function Rendered({ text }: { text: string }) {
  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      skipHtml
      disallowedElements={["img"]}
      unwrapDisallowed
      components={{
        a: ({ href, children }) => (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        ),
      }}
    >
      {text}
    </Markdown>
  );
}

function CopyButton({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      kind="quiet"
      size="sm"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setDone(true);
          setTimeout(() => {
            setDone(false);
          }, 1200);
        });
      }}
    >
      {done ? "copied" : "copy"}
    </Button>
  );
}

function usageWords(t: Turn): string | undefined {
  if (t.usage === undefined) {
    return undefined;
  }
  const parts = [`${String(t.usage.input_tokens)} in`, `${String(t.usage.output_tokens)} out`];
  if (t.usage.cost !== undefined) {
    parts.push(t.usage.cost);
  }
  return parts.join(" · ");
}

function TurnView({ t, who, model }: { t: Turn; who: string; model: string | undefined }) {
  const usage = usageWords(t);
  return (
    <div className={"chat-turn " + t.role}>
      <span className="chat-who">{who}</span>
      <div className="chat-bubble">{t.role === "assistant" ? <Rendered text={t.text} /> : t.text}</div>
      {t.role === "assistant" && (
        <div className="chat-foot">
          {model !== undefined && <span>{model}</span>}
          {usage !== undefined && <span>{usage}</span>}
          {t.cancelled === true && <span>stopped</span>}
          {t.lost === true && <span>the connection ended here</span>}
          <CopyButton text={t.text} />
        </div>
      )}
    </div>
  );
}

function ThreadView({
  opened,
  turns,
  reading,
  openError,
  streaming,
  note,
  model,
  modelName,
  card,
}: {
  /** Whether a thread is open, rather than a new one about to start. */
  opened: boolean;
  turns: Turn[];
  /** Whether the open thread is still being read. */
  reading: boolean;
  openError: string | undefined;
  /** The turn in flight on this thread, when one is. */
  streaming: Streaming | undefined;
  /** What the thread says after its last turn. */
  note: Note | undefined;
  /** The model the thread talks to, by name. */
  model: string | undefined;
  modelName: (workload: string | undefined) => string | undefined;
  /** The Approvals card, where the last turn escalated. */
  card: ReactNode;
}) {
  const end = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [follow, setFollow] = useState(true);
  const reply = streaming?.reply;

  // Keep the newest reply in view while it streams, unless the person scrolled up.
  useEffect(() => {
    if (follow) {
      end.current?.scrollIntoView({ block: "end" });
    }
  }, [turns.length, reply?.text, card, follow]);

  const onScroll = () => {
    const b = box.current;
    if (b === null) {
      return;
    }
    setFollow(b.scrollHeight - b.scrollTop - b.clientHeight < 40);
  };

  if (!opened && streaming === undefined && note === undefined) {
    return (
      <div className="chat-empty">
        <h2>Chat with a model of this workspace</h2>
        <p>Pick a model above and ask something. Your threads are kept for you, on any browser.</p>
      </div>
    );
  }
  const leftOut = reply !== undefined && reply.left_out > 0 ? reply.left_out : note?.leftOut;
  return (
    <div className="chat-thread" ref={box} onScroll={onScroll}>
      {reading && <div className="chat-note">Reading the thread…</div>}
      {openError !== undefined && <div className="chat-note error">{openError}</div>}
      {turns.map((t) => (
        <TurnView
          key={t.n}
          t={t}
          who={t.role === "user" ? "You" : (modelName(t.model) ?? "Model")}
          model={modelName(t.model)}
        />
      ))}
      {streaming !== undefined && streaming.text !== "" && (
        <TurnView t={{ n: 0, role: "user", text: streaming.text, at: "" }} who="You" model={undefined} />
      )}
      {note?.unsent !== undefined && (
        <TurnView t={{ n: 0, role: "user", text: note.unsent, at: "" }} who="You" model={undefined} />
      )}
      {leftOut !== undefined && (
        <div className="chat-note">
          {leftOut === 1 ? "1 older turn was" : `${String(leftOut)} older turns were`} left out: the model
          reads only so much.
        </div>
      )}
      {reply !== undefined && !reply.ended && (
        <div className="chat-turn assistant">
          <span className="chat-who">{model ?? "Model"}</span>
          {reply.text !== "" && (
            <div className="chat-bubble">
              <Rendered text={reply.text} />
            </div>
          )}
          <span className="chat-thinking">
            <span className="chat-pulse" />
            {!reply.started ? "sending…" : reply.thinking || reply.text === "" ? "thinking…" : "writing…"}
          </span>
        </div>
      )}
      {card !== undefined && <div className="chat-card">{card}</div>}
      {note?.error !== undefined && <div className="chat-note error">{note.error}</div>}
      {note?.lost === true && (
        <div className="chat-note error">
          The connection ended while the model was answering; what arrived is kept.
        </div>
      )}
      {!follow && (
        <div className="chat-note">
          <Button
            kind="quiet"
            size="sm"
            onClick={() => {
              setFollow(true);
              end.current?.scrollIntoView({ block: "end" });
            }}
          >
            ↓ newest
          </Button>
        </div>
      )}
      <div ref={end} />
    </div>
  );
}

function Composer({
  disabled,
  streaming,
  onSend,
  onStop,
}: {
  disabled: boolean;
  streaming: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
}) {
  const [text, setText] = useState("");
  const send = () => {
    const t = text.trim();
    if (t === "" || disabled || streaming) {
      return;
    }
    onSend(t);
    setText("");
  };
  return (
    <div className="chat-composer">
      <textarea
        aria-label="Message"
        placeholder={disabled ? "Not connected" : "Ask something — Enter sends, Shift+Enter is a new line"}
        value={text}
        disabled={disabled}
        rows={Math.min(8, Math.max(1, text.split("\n").length))}
        onChange={(ev) => {
          setText(ev.target.value);
        }}
        onKeyDown={(ev) => {
          if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            send();
          }
        }}
      />
      {streaming ? (
        <Button onClick={onStop}>Stop</Button>
      ) : (
        <Button kind="primary" onClick={send} disabled={disabled || text.trim() === ""}>
          Send
        </Button>
      )}
    </div>
  );
}

function ThreadList({
  threads,
  error,
  activeId,
  onNew,
  onSelect,
  onRename,
  onDelete,
  onClear,
}: {
  /** The person's threads, newest first; undefined while they are read. */
  threads: Summary[] | undefined;
  error: string | undefined;
  activeId: string | undefined;
  onNew: () => void;
  onSelect: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onDelete: (id: string) => void;
  onClear: () => void;
}) {
  const [editing, setEditing] = useState<{ id: string; title: string } | undefined>(undefined);
  const commit = () => {
    if (editing !== undefined) {
      const title = editing.title.trim();
      if (title !== "") {
        onRename(editing.id, title);
      }
      setEditing(undefined);
    }
  };
  return (
    <aside className="chat-list">
      <header>
        <strong>Threads</strong>
        <Button kind="primary" size="sm" onClick={onNew}>
          New
        </Button>
      </header>
      <ul>
        {(threads ?? []).map((c) => (
          <li key={c.id} className={c.id === activeId ? "active" : ""}>
            {editing?.id === c.id ? (
              <input
                autoFocus
                aria-label="Title"
                value={editing.title}
                onChange={(ev) => {
                  setEditing({ id: c.id, title: ev.target.value });
                }}
                onBlur={commit}
                onKeyDown={(ev) => {
                  if (ev.key === "Enter") {
                    commit();
                  } else if (ev.key === "Escape") {
                    setEditing(undefined);
                  }
                }}
              />
            ) : (
              <>
                <button
                  type="button"
                  className="chat-title"
                  title={`${c.title} — double-click to rename`}
                  onClick={() => {
                    onSelect(c.id);
                  }}
                  onDoubleClick={() => {
                    setEditing({ id: c.id, title: c.title });
                  }}
                >
                  {c.title || "Untitled"}
                </button>
                <span className="chat-when">{relative(Date.parse(c.updated))}</span>
                <span className="chat-x">
                  <Button
                    kind="icon"
                    size="sm"
                    label={`Delete ${c.title}`}
                    onClick={() => {
                      onDelete(c.id);
                    }}
                  >
                    ×
                  </Button>
                </span>
              </>
            )}
          </li>
        ))}
        {threads === undefined && error === undefined && <li className="chat-none">Reading your threads…</li>}
        {threads !== undefined && threads.length === 0 && <li className="chat-none">No threads yet.</li>}
        {error !== undefined && <li className="chat-none error">{error}</li>}
      </ul>
      <footer>
        <span>Kept in this workspace.</span>
        {threads !== undefined && threads.length > 0 && (
          <Confirm
            label="Clear"
            consequence="Deletes every thread of yours; they cannot be read again."
            onConfirm={onClear}
          />
        )}
      </footer>
    </aside>
  );
}
